import { translateCv } from './cv-builder/cvLanguage';
import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { Email, Phone, LinkedIn, GitHub } from '@mui/icons-material';

const SectionTitle = ({ children }) => (
  // Nota: Ajusté el mb a 0.5 y añadí mt para mejor espaciado entre secciones
  <Box sx={{ borderBottom: '2px solid #000', mb: 0.5, mt: 2 }}>
    <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#000' }}>
      {children.toUpperCase()}
    </Typography>
  </Box>
);

const CurriculumDisplay = React.forwardRef(({ data, language = 'es' }, ref) => {
  const t = text => translateCv(language, text);
  const { personalInfo, introduction, education, experience, research, skills, areasOfSpecialization, courses, languages } = data;

  // Estilo general para imitar la estructura de una sola columna y el tamaño A4
  const style = {
    fontFamily: 'Arial, sans-serif',
    fontSize: '10px',
    lineHeight: 1.4,
    color: '#000',
    padding: '30px 60px',
    margin: '0 auto',
    width: '210mm', // Ancho de A4
    minHeight: '297mm', // Alto de A4
    backgroundColor: '#fff',
    border: '1px solid #eee', // Borde solo para visualización
  };
  
  // Función para renderizar listas de detalles/descripciones (ul/li)
  const renderDescriptionList = (items) => (
    <ul style={{ paddingLeft: '15px', marginTop: '0', marginBottom: '5px' }}>
      {/* Filtra elementos vacíos o nulos para evitar viñetas sin texto */}
      {items.filter(item => item && item.trim().length > 0).map((item, index) => (
        <li key={index} style={{ marginBottom: '3px' }}>
          {item}
        </li>
      ))}
    </ul>
  );

  return (
    <Box id="cv-display" sx={style} ref={ref}>
      {/* CABECERA - Información Personal */}
      <Box sx={{ mb: 2, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
          {personalInfo.name}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: "center", textAlign: "center", flexWrap: 'wrap', gap: 1, fontSize: '10px' }}>
          <Box sx={{ display: 'flex', justifyContent: "center", textAlign: "center", alignItems: 'center' }}><Phone fontSize="inherit" sx={{ mr: 0.5 }} /> {personalInfo.phone} |</Box>
          <Box sx={{ display: 'flex', justifyContent: "center", textAlign: "center", alignItems: 'center' }}><Email fontSize="inherit" sx={{ mr: 0.5 }} /> {personalInfo.email}</Box>
        </Box>
        {personalInfo.linkedin !== '' && <Box sx={{ display: 'flex', justifyContent: "center", textAlign: "center", flexWrap: 'wrap', gap: 1, fontSize: '10px' }}>
          <Box sx={{ display: 'flex', justifyContent: "center", textAlign: "center", alignItems: 'center' }}><LinkedIn fontSize="inherit" sx={{ mr: 0.5 }} /> <a href={personalInfo.linkedin} target="_blank" rel="noopener noreferrer">{personalInfo.linkedin}</a></Box>
        </Box>}
        {personalInfo.github !== '' && <Box sx={{ display: 'flex', justifyContent: "center", textAlign: "center", flexWrap: 'wrap', gap: 1, fontSize: '10px' }}>
          <Box sx={{ display: 'flex', justifyContent: "center", textAlign: "center", alignItems: 'center' }}><GitHub fontSize="inherit" sx={{ mr: 0.5 }} /> <a href={personalInfo.github} target="_blank" rel="noopener noreferrer">{personalInfo.github}</a></Box>
        </Box>}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* CONTENIDO PRINCIPAL - UNA SOLA COLUMNA (Se elimina el Grid) */}
      <Box> 
        
        {/* Introducción */}
        <SectionTitle>{t('Introducción')}</SectionTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {introduction}
        </Typography>

        {/* Educación */}
        <SectionTitle>{t('Educación')}</SectionTitle>
        {education.map((item, index) => (
          <Box key={index} sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" fontWeight="bold">{item.degree}</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontStyle: 'italic' }}>{item.startDate} - {item.endDate}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" fontStyle="italic">{item.institution}</Typography>
              <Typography variant="body2">{item.city}</Typography>
            </Box>
            {item.details.length > 0 && renderDescriptionList(item.details)}
          </Box>
        ))}

        {/* Experiencia */}
        <SectionTitle>{t('Experiencia')}</SectionTitle>
        {experience.map((item, index) => (
          <Box key={index} sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" fontWeight="bold">{item.title}</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontStyle: 'italic' }}>{item.startDate} - {item.endDate}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" fontStyle="italic">{item.company}</Typography>
              <Typography variant="body2">{item.city}</Typography>
            </Box>
            {renderDescriptionList(item.description)}
          </Box>
        ))}
        
        {/* Investigación/Otros Trabajos (Si aplica) */}
        {research.length > 0 && (
          <>
            <SectionTitle>{t('Investigación/Otros Trabajos')}</SectionTitle>
            {research.map((item, index) => (
              <Box key={index} sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontWeight="bold">{item.title}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontStyle: 'italic' }}>{item.startDate} - {item.endDate}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontStyle="italic">{item.company}</Typography>
                  <Typography variant="body2">{item.city}</Typography>
                </Box>
                {renderDescriptionList(item.description)}
              </Box>
            ))}
          </>
        )}

        {/* Habilidades */}
        <SectionTitle>{t('Habilidades')}</SectionTitle>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
            {/* Se muestran las habilidades separadas por un punto (•) para un look compacto */}
            {skills.join(' • ')}
        </Typography>

        {/* Áreas de Especialización */}
        {areasOfSpecialization.length > 0 && (
          <>
            <SectionTitle>{t('Áreas de Especialización')}</SectionTitle>
            {areasOfSpecialization.map((item, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight="bold">{item.title}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontStyle="italic">{item.institution}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{item.date}</Typography>
                </Box>
              </Box>
            ))}
          </>
        )}

        {/* Cursos */}
        {courses.length > 0 && (
          <>
            <SectionTitle>{t('Cursos')}</SectionTitle>
            {courses.map((item, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontWeight="bold">{item.title}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{item.date}</Typography>
                </Box>
              </Box>
            ))}
          </>
        )}

        {/* Idiomas */}
        <SectionTitle>{t('Idiomas')}</SectionTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
            {/* Se muestran los idiomas separados por un punto (•) para un look compacto */}
            {languages.map(lang => `${lang.name}: ${lang.level}`).join(' • ')}
        </Typography>
        
      </Box>
    </Box>
  );
});

export default CurriculumDisplay;